import {AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import html2pdf, {Worker} from 'html2pdf.js';
import moment, {Moment} from "moment";
import "moment/locale/nl";
import {
    ConnectionType,
    DataType,
    DayOfWeekMeasurementsResult,
    DownloadableFile,
    GetUser,
    GroupedDayOfWeekMeasurements,
    Report,
    ReportScheduleResolution,
    ReportTimeRange,
    TimeResolution,
    UserProfile
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {ChartOptions, ChartOptionType, Dashboard, DashboardInfo} from "../../dashboard/dashboard.types";
import {
    asSourceIds,
    entityToSourceInfo,
    SourceInfo,
    SourcesProvider
} from "../../../utils/source-providers/sources-provider";
import {AppContext, CompletenessInfo} from "../../../app-context";
import {jsPDF} from "jspdf";
import {DashboardContext} from '../../dashboard/dashboard.context';
import {LocationProvider} from "../../../utils/source-providers/location-provider";
import {DateFieldRange, MomentDateFieldRange} from "../../../common/date/date-range/date-field-range";
import {DateRangeUtils} from "../../../common/date/date-range/date-range.utils";
import {QuarterlyReportUtils} from "../quarterly-report-utils";
import {getConnectionsExcel} from "../report-runner/report-runner.component";
import {AppCommonUtils, publishEvent} from "../../../common/app-common-utils";
import {Entity, EntityType} from "../../../handlers/entity";
import {Handler} from "../../../common/handler";
import {View} from "../../../common/view";
import {downloadConnectionsAsExcel} from "../../refdata/connections/connections-overview/connections-list-excel";
import {downloadBulkMonthData} from "../../refdata/connections/connections-overview/bulk-data-download";
import {
    DataQueryFilters,
    MeasurementsHandlerComponent,
    MeasurementsHandlerComponentData
} from "../../measurements-component/measurements-handler.component";
import {DashboardTime, TimeChangedEvent} from "../../../utils/chart-data-provider";
import {HandleQuery} from "../../../common/handle-query";
import {from, mergeMap, Observable, of} from "rxjs";
import {HandleEvent} from "../../../common/handle-event";
import {HandleCommand} from "../../../common/handle-command";
import {downloadableFileToDataUrl} from "../../refdata/locations/location-image.utils";
import {ChartUtilsService} from "../../charts/chart-utils.service";

@Component({
    selector: 'app-quarterly-report-html-to-pdf',
    templateUrl: './quarterly-report-html-to-pdf.component.html',
    styleUrls: ['./quarterly-report-html-to-pdf.component.scss'],
})
@Handler()
export class QuarterlyReportHtmlToPdfComponent extends View implements AfterViewInit {
    readonly weekdayLegend = DashboardContext.weekdaysSorted.map(day => ({
        key: day,
        label: day.charAt(0) + day.slice(1).toLowerCase(),
        color: DashboardContext.weekdayColors[day]
    }));

    appContext = AppContext;
    _report: Report;
    owner: UserProfile;
    sourceProvider: SourcesProvider<any>;
    timeRange: DashboardTime = DashboardContext.defaultRange();
    measurementsHandlerData: MeasurementsHandlerComponentData = {};

    @ViewChild("pdfContent") pdfContent: ElementRef;
    @ViewChild("measurementsHandler") measurementsHandler: MeasurementsHandlerComponent;
    @Output() allChartsRendered: EventEmitter<any> = new EventEmitter<any>();
    selectedLocationEntity: Entity = null;

    electricityConsumption: DataType = DataType.electricityConsumption;
    gasConsumption: DataType = DataType.gasConsumption;
    heatConsumption: DataType = DataType.heatConsumption;
    waterConsumption: DataType = DataType.waterConsumption;
    chargePointConsumption: DataType = DataType.chargePointConsumption;
    dashboard: Dashboard = DashboardContext.defaultDashboard();
    completenessData: CompletenessInfo[] = [];
    selectedReportYear: number = moment().year();
    selectedReportQuarter: number = Math.max(1, moment().quarter() - 1);
    quarterDayProfiles: ReportQuarterDayProfile[] = [];
    fullYearDisplayTimeRangeForQuarterlyReport: DashboardTime = null;
    locationImagePath: string;

    @ViewChild("dateRangeField", { read: ElementRef }) dateRangeField: ElementRef;

    ngAfterViewInit() {
        this.calculateResolution();
        if (this.dateRangeField) {
            publishEvent("timeChanged", <TimeChangedEvent>{
                timeRange: this.timeRange
            }, this.dateRangeField);
        }
        this.measurementsHandler.subscribeTo("getChartDataQueryFilters")
            .pipe(mergeMap((filters: DataQueryFilters) => this.measurementsHandler.sendQuery("getCompleteness", filters.timeRange)))
            .subscribe(c => {
                this.completenessData = c;
                setTimeout(() => this.allChartsRendered.emit(), 5000);
            });
    }

    @Input()
    set report(report: Report) {
        this._report = report;
        if (report) {
            this.selectedLocationEntity = null;
            this.selectedReportYear = this.currentYear;
            this.selectedReportQuarter = Math.max(1, this.completedQuarter || 1);
            this.calculateTimeRange();
            this.resolveSelectedLocation();
            if (report.owner === AppContext.userProfile.userId) {
                this.owner = AppContext.userProfile;
                this.renderReport();
            } else if (AppContext.isAdmin() && report.owner) {
                this.sendQuery("com.flowmaps.api.user.GetUser", <GetUser>{userId: report.owner}).subscribe(u => {
                    this.owner = u;
                    this.renderReport();
                })
            } else {
                this.renderReport();
            }
        }
    }

    hasSourceForConnectionType = (connectionType: ConnectionType | string) => this.sourceProvider
        ?.hasSourceForConnectionType(connectionType as ConnectionType);

    get isQuarterlyReport(): boolean {
        return true;
    }

    get reportYearOptions(): number[] {
        return Array.from({length: DashboardContext.maximumYears}, (_, index) => this.currentYear - index);
    }

    get currentYear(): number {
        return moment().year();
    }

    formatYear = (year: number) => String(year);

    formatQuarter = (quarter: number) => `Q${quarter}`;

    get quarterOptions(): number[] {
        const length = this.selectedReportYear < this.currentYear ? 4 : Math.max(0, this.completedQuarter);
        return Array.from({length}, (_, index) => index + 1);
    }

    get selectedLocation(): SourceInfo {
        return this.selectedLocationEntity ? entityToSourceInfo(this.selectedLocationEntity) : null;
    }

    get canGenerateReport(): boolean {
        return !!this._report && !!this.selectedLocation && (!this.isQuarterlyReport || this.completedQuarter > 0);
    }

    get validationMessage(): string {
        if (!this.selectedLocation) {
            return "Select a location to generate a quarterly report.";
        }
        if (this.isQuarterlyReport && this.completedQuarter === 0) {
            return `No quarter has been completed yet for ${this.selectedReportYear}.`;
        }
        return null;
    }

    get reportTitle(): string {
        return this._report?.info?.name;
    }

    get quarterLabel(): string {
        return this.selectedReportQuarter ? `Q${this.selectedReportQuarter} ${this.selectedReportYear}` : String(this.selectedReportYear);
    }

    get reportFileName(): string {
        const address = this.selectedLocation?.source?.location?.info?.address;
        const addressName = [address?.street, [address?.number, address?.addition].filter(v => v).join(""), address?.city]
            .filter(v => v).join("-").replace(/\s+/g, "-") || "location";
        return `Kwartaalrapport-Q${this.activeQuarter}-${this.selectedReportYear}-${addressName}.pdf`;
    }

    get completedQuarter(): number {
        if (!this.isQuarterlyReport) {
            return moment(this.timeRange.end).subtract(1, "day").quarter();
        }
        return this.selectedReportYear === this.currentYear
            ? Math.max(0, moment().quarter() - 1)
            : 4;
    }

    get selectedQuarter(): number {
        return Math.max(0, Math.min(4, this.selectedReportQuarter || 0));
    }

    get activeQuarter(): number {
        return this.selectedReportYear < this.currentYear ? this.selectedQuarter : this.completedQuarter;
    }

    get reportingWindowLabel(): string {
        return `${moment(this.timeRange.start).format("DD MMM YYYY")} - ${moment(this.timeRange.end).subtract(1, "day").format("DD MMM YYYY")}`;
    }

    get reportSelectedSources(): SourceInfo[] {
        return this.selectedLocation ? [this.selectedLocation] : [];
    }

    get reportAllSelectedSources(): SourceInfo[] {
        return this.reportSelectedSources;
    }

    get holidayRows(): OfficialHolidayRow[] {
        return this.getHolidayYears()
            .flatMap(year => this.getOfficialHolidaysForYear(year))
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((row, index) => ({
                ...row,
                color: this.getHolidayColor(index)
            }));
    }

    get hasHolidaySection(): boolean {
        return this.hasDayProfiles(this.electricityConsumption, true)
            || this.hasDayProfiles(this.gasConsumption, true)
            || this.hasDayProfiles(this.heatConsumption, true)
            || this.hasDayProfiles(this.waterConsumption, true)
            || this.hasDayProfiles(this.chargePointConsumption, true);
    }

    get hasGasOrHeatSources(): boolean {
        return this.hasSourceForConnectionType(ConnectionType.Gas)
            || this.hasSourceForConnectionType(ConnectionType.Heat);
    }

    get energyProfileChartTitle(): string {
        return ChartUtilsService.getEnergyProfileChartTitleKey(this.selectedLocation?.source?.location);
    }

    hasDayProfiles(dataType: DataType, holidaysOnly: boolean = false): boolean {
        const dataTypes = dataType === DataType.electricityConsumption
            ? [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak]
            : [dataType];
        return this.quarterDayProfiles.some(profile =>
            Object.values((holidaysOnly ? profile.result?.holidayMeasurements : profile.result?.measurements) || {}).some(dayMeasurements =>
                dataTypes.some(type => (dayMeasurements?.[type] || []).length > 0)));
    }

    get rangeResolution(): string {
        switch (this._report.info.query.range.resolution) {
            case ReportScheduleResolution.year:
                return "Annual";
            case ReportScheduleResolution.month:
                return "Monthly";
            case ReportScheduleResolution.week:
                return "Weekly";
            case ReportScheduleResolution.day:
                return "Daily";
        }
    }

    calculateTimeRange = () => {
        if (this.isQuarterlyReport) {
            const quarterlyRange = this.getQuarterRangeForYear(this.selectedReportYear, this.activeQuarter);
            this.timeRange = {
                start: quarterlyRange.start.toISOString(),
                end: quarterlyRange.end.toISOString(),
                label: quarterlyRange.label
            };
            this.updateQuarterlyReportDisplayTimeRange();
            return;
        }
        const range = this._report.info.query.range;
        const start = moment().subtract(range.value, range.resolution).startOf(range.resolution);
        this.timeRange = {
            start: start.toISOString(),
            end: start.clone().add(1, range.resolution).toISOString()
        };
        this.timeRange.label = DateRangeUtils.getRangeLabel({
            start: moment(this.timeRange.start),
            end: moment(this.timeRange.end)
        }, QuarterlyReportUtils.defaultRanges);
        this.updateQuarterlyReportDisplayTimeRange();
    }

    private updateQuarterlyReportDisplayTimeRange() {
        this.fullYearDisplayTimeRangeForQuarterlyReport = this.isQuarterlyReport ? {
            start: moment().year(this.selectedReportYear).startOf("year").toISOString(),
            end: moment().year(this.selectedReportYear + 1).startOf("year").toISOString(),
            resolution: TimeResolution.month
        } : null;
    }

    private getQuarterRangeForYear(year: number, quarter: number): MomentDateFieldRange {
        const selectedQuarter = Math.max(1, Math.min(4, quarter || 1));
        const start = moment().year(year).startOf("year");
        const end = moment().year(year).quarter(selectedQuarter).add(1, "quarter").startOf("quarter");
        return {
            label: `Quarterly report (Q${selectedQuarter} ${year})`,
            start, end
        };
    }

    private getHolidayYears(): number[] {
        if (this.isQuarterlyReport) {
            return [this.selectedReportYear];
        }

        const start = moment(this.timeRange?.start).startOf("day");
        const end = moment(this.timeRange?.end).startOf("day");
        if (!start.isValid() || !end.isValid() || !start.isBefore(end)) {
            return [];
        }

        const years = new Set<number>();
        let cursor = start.clone();
        while (cursor.isBefore(end)) {
            years.add(cursor.year());
            cursor.add(1, "year").startOf("year");
        }

        return Array.from(years).sort((a, b) => a - b);
    }

    renderReport() {
        this.syncReportState();
    }

    private updateMeterOptions(info: DashboardInfo) {
        this.setMeterOptions(ChartOptionType.Electricity, info);
        this.setMeterOptions(ChartOptionType.Gas, info);
        this.setMeterOptions(ChartOptionType.Heat, info);
        this.setMeterOptions(ChartOptionType.Water, info);
        this.setMeterOptions(ChartOptionType.ChargePoint, info);
    }

    private setMeterOptions(chartOptionType: ChartOptionType, info: DashboardInfo) {
        info.chartOptions[chartOptionType].showAsTable = true;
        info.chartOptions[chartOptionType].splitOffPeak = false;
        info.chartOptions[chartOptionType].showCompleteness = false;
        info.chartOptions[chartOptionType].compare = {
            relative: true,
            comparedYear: 1,
            enabled: true
        }
    }

    sourceSelectionUpdated() {
        this._report.info.query.sources = asSourceIds(this.reportSelectedSources);
        this.syncReportState();
    }

    clearSelectedLocation() {
        this.selectedLocationEntity = null;
        this.locationImagePath = null;
        this.sourceSelectionUpdated();
    }

    private syncReportState() {
        const info = this.dashboard.info;
        info.timeRange = this.timeRange;
        info.resolution = this._report.info.query.resolution;
        info.sources = this._report.info.query.sources;
        this.updateMeterOptions(info);
        this.updateSourceProvider();
        this.measurementsHandlerData = {
            ...this.measurementsHandlerData,
            chartOptions: info.chartOptions,
            timeRange: {
                ...this.timeRange,
                label: info.predefinedTimeRange,
                resolution: info.resolution
            },
            selectedSources: this.reportSelectedSources,
            allSelectedSources: this.reportAllSelectedSources
        };
        this.loadQuarterDayProfiles();
        this.loadSelectedLocationImage();
    }

    private loadSelectedLocationImage() {
        this.locationImagePath = null;
        const locationId = this.selectedLocationEntity?.location?.locationId;
        if (!locationId) {
            return;
        }
        this.sendQuery("com.flowmaps.api.organisation.GetLocationImage", {locationId}, {
            caching: false,
            hideError: true
        }).subscribe((file: DownloadableFile) => this.locationImagePath = downloadableFileToDataUrl(file));
    }

    private updateSourceProvider() {
        if (!this.selectedLocation) {
            this.sourceProvider = null;
            return;
        }
        this.sourceProvider = new LocationProvider();
        this.sourceProvider.selectedSources = this._report.info.query.sources;
        this.sourceProvider.collectData();
    }

    searchLocations = (term: string) => this.sendQuery("searchLocations", term || "");

    locationFormatter = (location: Entity) => location?.getFormattedName() || "";

    locationResultFormatter = (location: Entity) => {
        const locationName = this.locationFormatter(location);
        const organisationName = location?.organisation?.info?.name || location?.organisation?.organisationId || "";
        return organisationName && organisationName !== locationName
            ? `${locationName} - ${organisationName}`
            : locationName;
    }

    timeRangeChanged = (value: DateFieldRange) => {
        if (this.isQuarterlyReport) {
            return;
        }
        this.timeRange = value;
        if (value) {
            this.calculateResolution();
        }
        publishEvent("timeChanged", <TimeChangedEvent> {
            timeRange: {
                resolution: this._report.info.query.resolution,
                ...this.timeRange
            }
        }, this.dateRangeField);
    }

    calculateResolution = () => {
        if (this.isQuarterlyReport) {
            this._report.info.query.range = {
                resolution: ReportScheduleResolution.year,
                value: 0
            };
            this.timeRange.resolution = TimeResolution.month;
            this._report.info.query.resolution = TimeResolution.month;
            return;
        }
        this._report.info.query.range = this.calculateReportRange(this.timeRange);
        this.timeRange.resolution = AppContext.resolutionForTimeRange(this.timeRange);
        this._report.info.query.resolution = this.timeRange.resolution;
    }

    reportYearChanged(year: number) {
        this.selectedReportYear = year;
        this.selectedReportQuarter = year < this.currentYear ? 4 : Math.max(1, this.completedQuarter || 1);
        if (!this._report || !this.isQuarterlyReport) {
            return;
        }
        this.calculateTimeRange();
        this.calculateResolution();
        this.renderReport();
    }

    reportQuarterChanged(quarter: number) {
        this.selectedReportQuarter = quarter;
        if (!this._report || !this.isQuarterlyReport) {
            return;
        }
        this.calculateTimeRange();
        this.calculateResolution();
        this.renderReport();
    }

    calculateReportRange = (range: DateFieldRange): ReportTimeRange => {
        if (!range.label) {
            return {};
        }
        const start = moment(range.start);
        const end = moment(range.end);
        const reportRangeNow = this.getReportTimeRange(start, moment());
        const reportRangeEnd = this.getReportTimeRange(start, end);
        return reportRangeNow.resolution === reportRangeEnd.resolution
            ? reportRangeNow : {
                resolution: reportRangeEnd.resolution,
                value: Math.max(0, reportRangeEnd.value - 1)
            };
    }

    getReportTimeRange = (start: Moment, end: Moment): ReportTimeRange => {
        const duration = moment.duration(end.diff(start));
        const resolution = this.getResolutionOfDuration(duration);
        return {
            resolution: resolution,
            value: duration.get(resolution)
        }
    }

    private getResolutionOfDuration(duration: moment.Duration) {
        return Math.round(duration.asYears()) > 0 ? ReportScheduleResolution.year
            : Math.round(duration.asMonths()) > 0 ? ReportScheduleResolution.month
                : Math.round(duration.asWeeks()) > 0 ? ReportScheduleResolution.week
                    : ReportScheduleResolution.day;
    }

    async createPdf(): Worker<jsPDF> {
        const options = {
            margin: [0.5, 0, 0.3, 0],
            filename: this.reportFileName,
            image: {type: 'jpeg', quality: 0.98},
            html2canvas: {scale: 2, logging: true, dpi: 192, letterRendering: true},
            pagebreak: {before: '.page-break', mode: ['avoid-all', 'css', 'legacy']},
            jsPDF: {unit: 'in', format: 'a4', orientation: 'portrait'}
        };
        return html2pdf().set(options).from(this.pdfContent.nativeElement).toPdf().get('pdf');
    }

    download() {
        if (!this.canGenerateReport) {
            return;
        }
        AppCommonUtils.waitForProcess(from(this.createPdf())).subscribe({
            next: (pdf: jsPDF) => {
                pdf.save(this.reportFileName);
                getConnectionsExcel(this.elementRef, this.sourceProvider, downloadConnectionsAsExcel).subscribe();
                downloadBulkMonthData(this.sourceProvider, this.timeRange);
            },
            error: error => AppCommonUtils.registerError(error)
        });
    }

    @HandleQuery("getTimeRanges")
    getTimeRanges(): Observable<MomentDateFieldRange[]> {
        return of(QuarterlyReportUtils.defaultRanges);
    }

    @HandleEvent("filtersChanged")
    onFiltersChanged(filters: DataQueryFilters, ev: CustomEvent) {
        ev.stopPropagation();
        return false;
    }

    @HandleCommand("selectEntity")
    selectEntity(entityId: string) {
        this.sourceProvider.selectSourceById(entityId);
    }

    @HandleQuery("getWrappedChartOptions")
    getWrappedChartOptions(type: ChartOptionType): Observable<ChartOptions> {
        return of(this.dashboard.info.chartOptions[type]);
    }

    completenessTitle = (connectionTypes: ConnectionType[] | string[]): string =>
        AppContext.completenessTitle(this.completenessData, connectionTypes, this.sourceProvider
            ?.getAllSourcesByType(EntityType.connection) || []);

    completeness = (connectionTypes: ConnectionType[] | string[]): number =>
        AppContext.completeness(this.completenessData, connectionTypes, this.sourceProvider
            ?.getAllSourcesByType(EntityType.connection) || []);

    completenessColor = (connectionTypes: ConnectionType[] | string[]): string =>
        AppContext.completenessColor(this.completenessData, connectionTypes, this.sourceProvider
            ?.getAllSourcesByType(EntityType.connection) || []);

    private loadQuarterDayProfiles() {
        if (!this.isQuarterlyReport || !this.selectedLocation || !this._report?.info?.query?.sources) {
            this.quarterDayProfiles = [];
            return;
        }

        const quarterRanges = this.getQuarterRanges(this.selectedReportYear, this.activeQuarter);
        if (!quarterRanges.length) {
            this.quarterDayProfiles = [];
            return;
        }

        this.sendQuery("com.flowmaps.api.measurements.GetGroupedDayOfWeekMeasurements", {
            sources: this._report.info.query.sources,
            timeRanges: quarterRanges.map(range => ({
                start: range.timeRange.start,
                end: range.timeRange.end
            }))
        }).subscribe((results: GroupedDayOfWeekMeasurements[]) => {
            this.quarterDayProfiles = quarterRanges.map((range, index) => ({
                ...range,
                result: results?.[index]?.result || <DayOfWeekMeasurementsResult>{measurements: {}, holidayMeasurements: {}}
            }));
        });
    }

    private getQuarterRanges(year: number, quarterCount: number): ReportQuarterRange[] {
        const limitedQuarterCount = Math.max(0, Math.min(4, quarterCount || 0));
        return Array.from({length: limitedQuarterCount}, (_, index) => {
            const quarter = index + 1;
            const start = moment().year(year).quarter(quarter).startOf("quarter");
            return {
                quarter,
                label: `Q${quarter} ${year}`,
                timeRange: {
                    start: start.toISOString(),
                    end: start.clone().add(1, "quarter").toISOString()
                }
            };
        });
    }

    private resolveSelectedLocation() {
        const locationIds = new Set(this._report?.info?.query?.sources?.locationIds?.filter(Boolean) || []);
        if (!locationIds.size) {
            this.selectedLocationEntity = null;
            this.selectOnlyAvailableLocation();
            return;
        }
        this.sendQuery("getLocationsAsEntities").subscribe((locations: Entity[]) => {
            this.selectedLocationEntity = locations?.find(location => {
                const ids = [location.getEntityId(), ...(location.location?.aliasIds || [])].filter(Boolean);
                return ids.some(id => locationIds.has(id));
            }) || null;
            this.syncReportState();
        });
    }

    private selectOnlyAvailableLocation() {
        this.sendQuery("getLocationsAsEntities").subscribe((locations: Entity[]) => {
            if (locations?.length !== 1 || this.selectedLocationEntity) {
                return;
            }
            this.selectedLocationEntity = locations[0];
            this.sourceSelectionUpdated();
        });
    }

    private getOfficialHolidaysForYear(year: number): OfficialHolidayRow[] {
        const easterSunday = this.getEasterSunday(year);
        const kingsDay = this.getKingsDay(year);

        return [
            this.createHolidayRow(moment({year, month: 0, date: 1}), "New Year's Day"),
            this.createHolidayRow(easterSunday.clone(), "Easter Sunday"),
            this.createHolidayRow(easterSunday.clone().add(1, "day"), "Easter Monday"),
            this.createHolidayRow(kingsDay, "King's Day"),
            this.createHolidayRow(easterSunday.clone().add(39, "days"), "Ascension Day"),
            this.createHolidayRow(easterSunday.clone().add(49, "days"), "Pentecost Sunday"),
            this.createHolidayRow(easterSunday.clone().add(50, "days"), "Pentecost Monday"),
            this.createHolidayRow(moment({year, month: 11, date: 25}), "Christmas Day"),
            this.createHolidayRow(moment({year, month: 11, date: 26}), "Boxing Day")
        ];
    }

    private createHolidayRow(date: moment.Moment, holidayName: string): OfficialHolidayRow {
        return {
            date: date.format("YYYY-MM-DD"),
            dateLabel: date.clone().locale(AppCommonUtils.getPreferredLanguage()).format("D MMMM"),
            holidayLabel: holidayName
        };
    }

    private getHolidayColor(index: number): string {
        const hue = (index * 47) % 360;
        return `hsl(${hue} 68% 52%)`;
    }

    private getKingsDay(year: number): moment.Moment {
        const kingsDay = moment({year, month: 3, date: 27});
        return kingsDay.day() === 0 ? kingsDay.subtract(1, "day") : kingsDay;
    }

    private getEasterSunday(year: number): moment.Moment {
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        return moment({year, month: month - 1, date: day});
    }
}

interface OfficialHolidayRow {
    date: string;
    dateLabel: string;
    holidayLabel: string;
    color?: string;
}

interface ReportQuarterRange {
    quarter: number;
    label: string;
    timeRange: {
        start: string;
        end: string;
    };
}

interface ReportQuarterDayProfile extends ReportQuarterRange {
    result: DayOfWeekMeasurementsResult;
}
